import { useMatchRoute } from '@tanstack/react-router'
import { Link } from 'lucide-react'
import { type MouseEvent, type ReactElement, use } from 'react'

import { useOpenChat, useTranslate } from '@orus.eu/pharaoh'
import { MapIllustration } from '@orus.eu/pharaoh-next/components/illustrations/artworks/map'
import { Avatar } from '@orus.eu/pharaoh-next/components/ui/avatar'
import { Button } from '@orus.eu/pharaoh-next/components/ui/button'
import { Empty } from '@orus.eu/pharaoh-next/components/ui/empty'
import { Text } from '@orus.eu/pharaoh-next/components/ui/text'
import { View } from '@orus.eu/pharaoh-next/components/ui/view'
import { cn } from '@orus.eu/pharaoh-next/lib/utils'
import { TrackButtonClickContext } from '@orus.eu/pharaoh/src/wrapper-context'

import { PharaohNextScope } from '#/lib/pharaoh-next/pharaoh-next-scope.tsx'

export function NotFoundPage({ message }: { message?: string }): ReactElement {
  const translate = useTranslate()
  const openChat = useOpenChat()
  const trackButtonClick = use(TrackButtonClickContext)
  const matchRoute = useMatchRoute()
  const surface = matchRoute({ to: '/bo', fuzzy: true, includeSearch: false }) ? 'bo' : 'fo'

  function contactSupport(event: MouseEvent<HTMLButtonElement>): void {
    trackButtonClick({
      buttonName: 'contact_button',
      buttonText: event.currentTarget.textContent || undefined,
      buttonIcon: undefined,
    })
    openChat()
  }

  return (
    <PharaohNextScope surface={surface}>
      <View.Root className="max-h-dvh min-h-0 overflow-y-auto">
        <View.Header>
          <View.Title>{translate('problem_not_found_title')}</View.Title>
        </View.Header>
        <View.Body>
          <Empty.Root size="large" className="gap-section px-0">
            <Empty.Media>
              <MapIllustration aria-hidden className="size-[140px]" />
            </Empty.Media>
            <Empty.Header className="gap-stack">
              <Avatar.Root aria-hidden>
                <Link />
              </Avatar.Root>
              <Empty.Title>{translate('problem_not_found_principal_message')}</Empty.Title>
              <Empty.Description className="flex min-w-0 flex-col gap-stack wrap-anywhere">
                <Text variant="body2" render={<p />}>
                  {message ?? translate('problem_not_found_first_subtext')}
                </Text>
                <Text variant="body2" render={<p />}>
                  {translate('problem_not_found_second_subtext')}
                </Text>
              </Empty.Description>
            </Empty.Header>
            <Empty.Content className="w-full">
              <Button
                onClick={contactSupport}
                mobileSize="large"
                className={cn(`
                  w-full
                  md:w-auto
                `)}
              >
                {translate('contact_customer_support')}
              </Button>
            </Empty.Content>
          </Empty.Root>
        </View.Body>
      </View.Root>
    </PharaohNextScope>
  )
}
